import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { cn } from "@/lib/cn";

export interface KpiCardProps {
  label: string;
  value: string;
  hint?: string | null;
  /** Optional trend series. When present, the card reveals a mini
   * sparkline on hover. Backward compatible — callers may omit it. */
  trend?: number[];
  className?: string;
}

/** Compact metric card. Large tabular number, tiny uppercased label,
 * optional hint for trend/context. On hover the card scales slightly,
 * the border intensifies, and — if a `trend` series is provided — a
 * 60px recharts sparkline slides up from the bottom edge. */
export function KpiCard({
  label,
  value,
  hint,
  trend,
  className,
}: KpiCardProps) {
  const [hover, setHover] = useState(false);
  const reduced = useReducedMotion();

  const hasTrend = Array.isArray(trend) && trend.length > 1;
  const trendData = hasTrend ? trend.map((v, i) => ({ i, v })) : [];

  return (
    <motion.div
      onHoverStart={() => setHover(true)}
      onHoverEnd={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      animate={
        reduced
          ? undefined
          : { scale: hover ? 1.02 : 1 }
      }
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      className={cn(
        "group relative overflow-hidden rounded-[16px] border bg-white/[0.03] px-5 py-4 transition-colors",
        hover
          ? "border-brand-1/60 shadow-[0_0_0_1px_rgba(167,139,250,0.3),0_12px_32px_-18px_rgba(167,139,250,0.55)]"
          : "border-border-1",
        className,
      )}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-text-4">
        {label}
      </div>
      <div
        className="mt-1 text-2xl font-bold font-display text-text-1"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-text-3">{hint}</div>}

      {hasTrend && (
        <AnimatePresence>
          {hover && (
            <motion.div
              aria-hidden
              initial={
                reduced ? { opacity: 0 } : { opacity: 0, y: 12 }
              }
              animate={{ opacity: 1, y: 0 }}
              exit={
                reduced ? { opacity: 0 } : { opacity: 0, y: 12 }
              }
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 28,
              }}
              className="pointer-events-none mt-3 h-[60px] w-full"
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={trendData}
                  margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
                >
                  <Line
                    type="monotone"
                    dataKey="v"
                    stroke="var(--color-brand-1)"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={!reduced}
                  />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </motion.div>
  );
}
